import { Indicator } from '@mantine/core'
import styles from './Stamp.module.css'

// ─────────────────────────────────────────────
// 型定義
// ─────────────────────────────────────────────
export type StampSize = 'XLarge' | 'Large' | 'Medium' | 'Small' | 'XSmall' | 'Mini'
export type StampType = 'Get' | 'None'
export type StampShape = 'circular' | 'square'

export type StampProps = {
  /**
   * 'Get': スタンプ獲得済み（src 画像を表示）
   * 'None': 未獲得（グレープレースホルダー）
   * デフォルト: 'None'
   */
  type?: StampType
  /** スタンプ画像の URL（type='Get' のときに使用） */
  src?: string
  /** 画像の alt テキスト */
  alt?: string
  /** サイズ（デフォルト: 'Medium'）
   * XLarge=266px / Large=128px / Medium=72px / Small=56px / XSmall=46px / Mini=32px
   */
  size?: StampSize
  /**
   * 形状（デフォルト: 'circular'）
   * 'circular': 円形（border-radius: 50%）
   * 'square': 四角（border-radius: 0）
   */
  shape?: StampShape
  className?: string
  /**
   * 獲得枚数。2 以上の場合にスタンプ右上にバッジを表示する。
   * 100 以上は '99+' と表示する。
   */
  count?: number
}

// ─────────────────────────────────────────────
// サイズマップ
// ─────────────────────────────────────────────
const SIZE_CLASS: Record<StampSize, string> = {
  XLarge: styles.sizeXLarge,
  Large: styles.sizeLarge,
  Medium: styles.sizeMedium,
  Small: styles.sizeSmall,
  XSmall: styles.sizeXSmall,
  Mini: styles.sizeMini,
}

// ─────────────────────────────────────────────
// 形状マップ
// ─────────────────────────────────────────────
const SHAPE_CLASS: Record<StampShape, string> = {
  circular: styles.shapeCircular,
  square: styles.shapeSquare,
}

// ─────────────────────────────────────────────
// 円形スタンプの Indicator offset（バッジ中心を円の輪郭に合わせる）
// 計算式: r * (1 - 1/√2)  where r = stamp_px / 2
// ─────────────────────────────────────────────
const CIRCULAR_INDICATOR_OFFSET: Record<StampSize, number> = {
  XLarge: 39, // 266/2 * 0.293
  Large: 19, // 128/2 * 0.293
  Medium: 11, //  72/2 * 0.293 ≈ 10.5 → 11
  Small: 8, //  56/2 * 0.293
  XSmall: 7, //  46/2 * 0.293
  Mini: 5, //  32/2 * 0.293
}

/**
 * スタンプコンポーネント。
 * type='Get' のとき src で指定した画像を表示し、
 * type='None' のときグレープレースホルダーを表示する。
 * shape='circular'（デフォルト）で円形、shape='square' で四角に表示する。
 * count が 2 以上のとき右上に Mantine Indicator バッジを表示する。
 *
 * @example
 * <Stamp type="Get" src="/stamps/rally1.png" size="Large" shape="circular" />
 * <Stamp type="Get" src="/stamps/rally1.png" size="Large" shape="square" />
 * <Stamp type="None" size="Medium" />
 * <Stamp type="Get" src="/stamps/rally1.png" size="Small" count={3} />
 */
export const Stamp = ({
  type = 'None',
  src,
  alt = '',
  size = 'Medium',
  shape = 'circular',
  className,
  count,
}: StampProps) => {
  const sizeClass = SIZE_CLASS[size]
  const shapeClass = SHAPE_CLASS[shape]

  const stampEl = (
    <div
      className={[styles.stamp, sizeClass, shapeClass, type === 'None' ? styles.none : '', className ?? '']
        .join(' ')
        .trim()}
    >
      {type === 'Get' && src && <img className={styles.image} src={src} alt={alt} />}
    </div>
  )

  if (count !== undefined && count >= 2) {
    const offset = shape === 'circular' ? CIRCULAR_INDICATOR_OFFSET[size] : 0
    return (
      <Indicator
        label={count >= 100 ? '99+' : String(count)}
        size={18}
        position="top-end"
        offset={offset}
        color="var(--main-color)"
        styles={{ indicator: { color: 'var(--on-main-color)', fontWeight: 700, fontSize: 12 } }}
      >
        {stampEl}
      </Indicator>
    )
  }

  return stampEl
}
